import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaProjectDiagram, FaFileInvoiceDollar, FaFileInvoice, FaTasks, FaExternalLinkAlt, FaSync, FaCalendarCheck, FaPlus, FaFileAlt, FaArrowRight, FaCheckCircle, FaClock } from 'react-icons/fa';
import { toast } from 'react-toastify';
import BusinessPartnerSalesService, { PartnerSalesSummary } from '../services/BusinessPartnerSalesService';
import OfferService, { Offer } from '../../sales/services/OfferService';
import InvoiceService, { Invoice } from '../../sales/services/InvoiceService';
import { Project } from '../services/ProjectService';

interface LastAction {
  type: 'project_created' | 'offer_created' | 'offer_sent' | 'offer_accepted' | 'invoice_created' | 'invoice_sent' | 'invoice_paid';
  label: string;
  date: string;
  relatedId?: number;
  relatedNumber?: string;
}

interface ContactOverviewPanelProps {
  partnerId: number;
  partnerCreatedAt?: string;
  projects?: Project[];
  onScrollToTasks?: () => void;
  onCreateOffer?: () => void;
  onCreateOfferFromTemplate?: () => void;
}

export default function ContactOverviewPanel({ partnerId, partnerCreatedAt, projects = [], onScrollToTasks, onCreateOffer, onCreateOfferFromTemplate }: ContactOverviewPanelProps): React.JSX.Element | null {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<PartnerSalesSummary | null>(null);
  const [recentOffers, setRecentOffers] = useState<Offer[]>([]);
  const [recentInvoices, setRecentInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    loadData();
  }, [partnerId]);

  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const [summaryData, offersData, invoicesData] = await Promise.all([
        BusinessPartnerSalesService.getSalesSummary(partnerId),
        OfferService.list({ partner_id: partnerId, ordering: '-created_at', page_size: 3 }),
        InvoiceService.list({ partner_id: partnerId, ordering: '-issue_date', page_size: 3 }),
      ]);
      setSummary(summaryData);
      setRecentOffers(offersData.slice(0, 3));
      setRecentInvoices(invoicesData.slice(0, 3));
    } catch (err) {
      console.error('Failed to load partner sales summary:', err);
      setError(true);
      toast.error('Failed to load customer overview');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatRelativeDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Heute';
    if (diffDays === 1) return 'Gestern';
    if (diffDays < 7) return `vor ${diffDays} Tagen`;
    return formatDate(dateString);
  };

  // Determine the last action and next suggested action
  const lastAction = useMemo((): LastAction | null => {
    const actions: { date: Date; action: LastAction }[] = [];

    // Check projects
    projects.forEach(project => {
      if (project.created_at) {
        actions.push({
          date: new Date(project.created_at),
          action: {
            type: 'project_created',
            label: `Projekt "${project.name}" angelegt`,
            date: project.created_at,
            relatedId: project.id,
          },
        });
      }
    });

    // Check offers
    recentOffers.forEach(offer => {
      if (offer.status === 'ACCEPTED' && offer.accepted_at) {
        actions.push({
          date: new Date(offer.accepted_at),
          action: {
            type: 'offer_accepted',
            label: `Angebot ${offer.offer_number} angenommen`,
            date: offer.accepted_at,
            relatedId: offer.id,
            relatedNumber: offer.offer_number,
          },
        });
      } else if (offer.status === 'SENT' && offer.sent_at) {
        actions.push({
          date: new Date(offer.sent_at),
          action: {
            type: 'offer_sent',
            label: `Angebot ${offer.offer_number} versendet`,
            date: offer.sent_at,
            relatedId: offer.id,
            relatedNumber: offer.offer_number,
          },
        });
      } else if (offer.created_at) {
        actions.push({
          date: new Date(offer.created_at),
          action: {
            type: 'offer_created',
            label: `Angebot ${offer.offer_number} erstellt`,
            date: offer.created_at,
            relatedId: offer.id,
            relatedNumber: offer.offer_number,
          },
        });
      }
    });

    // Check invoices - use created_at as primary date since paid_at/sent_at may not exist
    recentInvoices.forEach(invoice => {
      const invoiceDate = invoice.updated_at || invoice.created_at;
      if (invoice.status === 'PAID') {
        actions.push({
          date: new Date(invoiceDate),
          action: {
            type: 'invoice_paid',
            label: `Rechnung ${invoice.invoice_number} bezahlt`,
            date: invoiceDate,
            relatedId: invoice.id,
            relatedNumber: invoice.invoice_number,
          },
        });
      } else if (invoice.status === 'SENT') {
        actions.push({
          date: new Date(invoiceDate),
          action: {
            type: 'invoice_sent',
            label: `Rechnung ${invoice.invoice_number} versendet`,
            date: invoiceDate,
            relatedId: invoice.id,
            relatedNumber: invoice.invoice_number,
          },
        });
      } else if (invoice.created_at) {
        actions.push({
          date: new Date(invoice.created_at),
          action: {
            type: 'invoice_created',
            label: `Rechnung ${invoice.invoice_number} erstellt`,
            date: invoice.created_at,
            relatedId: invoice.id,
            relatedNumber: invoice.invoice_number,
          },
        });
      }
    });

    // Sort by date descending and return the most recent
    actions.sort((a, b) => b.date.getTime() - a.date.getTime());
    return actions.length > 0 ? actions[0].action : null;
  }, [projects, recentOffers, recentInvoices]);

  // Determine the suggested next action
  const suggestedAction = useMemo(() => {
    // If there are projects but no offers, suggest creating an offer
    if (projects.length > 0 && recentOffers.length === 0) {
      return { type: 'create_offer', label: 'Angebot erstellen' };
    }

    // If there are draft offers, suggest sending them
    const draftOffers = recentOffers.filter(o => o.status === 'DRAFT');
    if (draftOffers.length > 0) {
      return {
        type: 'send_offer',
        label: `Angebot ${draftOffers[0].offer_number} versenden`,
        offerId: draftOffers[0].id,
      };
    }

    // If offer was just accepted, suggest next steps
    const acceptedOffers = recentOffers.filter(o => o.status === 'ACCEPTED');
    if (acceptedOffers.length > 0) {
      return {
        type: 'view_offer',
        label: `AB erstellen für ${acceptedOffers[0].offer_number}`,
        offerId: acceptedOffers[0].id,
      };
    }

    // Default: suggest creating a new offer
    if (projects.length > 0) {
      return { type: 'create_offer', label: 'Neues Angebot erstellen' };
    }

    return null;
  }, [projects, recentOffers]);

  if (loading) {
    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid #e0e3ef',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '16px',
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                background: '#f1f5f9',
                borderRadius: '10px',
                padding: '16px 20px',
                height: '100px',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            >
              <div
                style={{
                  width: '60%',
                  height: '14px',
                  background: '#e2e8f0',
                  borderRadius: '4px',
                  marginBottom: '12px',
                }}
              />
              <div
                style={{
                  width: '40%',
                  height: '24px',
                  background: '#e2e8f0',
                  borderRadius: '4px',
                  marginBottom: '8px',
                }}
              />
              <div
                style={{
                  width: '50%',
                  height: '12px',
                  background: '#e2e8f0',
                  borderRadius: '4px',
                }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '12px',
          padding: '16px 24px',
          marginBottom: '24px',
          color: '#991b1b',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <span>Failed to load customer overview. Please refresh the page.</span>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const openTasks = (summary.tasks.OPEN ?? 0) + (summary.tasks.IN_PROGRESS ?? 0);
  const overdueTasks = summary.tasks.overdue ?? 0;
  const paymentColor = BusinessPartnerSalesService.getPaymentBehaviorColor(summary.payment_behavior.icon);
  const overdueRatioPercent = (summary.payment_behavior.details.overdue_ratio * 100).toFixed(1);

  const cardStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '12px',
    padding: '20px 24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  };

  const cardHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '1px solid #e2e8f0',
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
    marginBottom: '6px',
  };

  const getOfferStatusColor = (status: string): { bg: string; text: string } => {
    switch (status) {
      case 'DRAFT': return { bg: '#f3f4f6', text: '#374151' };
      case 'SENT': return { bg: '#dbeafe', text: '#1e40af' };
      case 'ACCEPTED': return { bg: '#d1fae5', text: '#065f46' };
      case 'REJECTED': return { bg: '#fee2e2', text: '#991b1b' };
      case 'EXPIRED': return { bg: '#fef3c7', text: '#92400e' };
      default: return { bg: '#f3f4f6', text: '#374151' };
    }
  };

  const getInvoiceStatusColor = (status: string): { bg: string; text: string } => {
    switch (status) {
      case 'DRAFT': return { bg: '#f3f4f6', text: '#374151' };
      case 'SENT': return { bg: '#dbeafe', text: '#1e40af' };
      case 'PAID': return { bg: '#d1fae5', text: '#065f46' };
      case 'CANCELLED': return { bg: '#fee2e2', text: '#991b1b' };
      default: return { bg: '#f3f4f6', text: '#374151' };
    }
  };

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e0e3ef',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
      }}
    >
      {/* Last Action & Quick Actions Section */}
      {(lastAction || suggestedAction) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid #bae6fd',
          }}
        >
          {/* Last Action */}
          {lastAction && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: '#0284c7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FaCheckCircle size={18} color="white" />
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#0369a1', fontWeight: 500, marginBottom: '2px' }}>
                  Letzte Aktion
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#0c4a6e' }}>
                  {lastAction.label}
                </div>
                <div style={{ fontSize: '11px', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                  <FaClock size={9} />
                  {formatRelativeDate(lastAction.date)}
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {suggestedAction?.type === 'create_offer' && onCreateOfferFromTemplate && (
              <>
                <button
                  onClick={onCreateOfferFromTemplate}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 18px',
                    background: '#021a4a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#0c3d7a'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#021a4a'; }}
                >
                  <FaFileAlt size={12} />
                  Angebot aus Vorlage
                  <FaArrowRight size={10} />
                </button>
                {onCreateOffer && (
                  <button
                    onClick={onCreateOffer}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '10px 16px',
                      background: 'white',
                      color: '#021a4a',
                      border: '1px solid #e0e3ef',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    <FaPlus size={10} />
                    Leeres Angebot
                  </button>
                )}
              </>
            )}
            {suggestedAction?.type === 'send_offer' && suggestedAction.offerId && (
              <button
                onClick={() => navigate(`/offers/${suggestedAction.offerId}`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 18px',
                  background: '#021a4a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {suggestedAction.label}
                <FaArrowRight size={10} />
              </button>
            )}
            {suggestedAction?.type === 'view_offer' && suggestedAction.offerId && (
              <button
                onClick={() => navigate(`/offers/${suggestedAction.offerId}`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 18px',
                  background: '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {suggestedAction.label}
                <FaArrowRight size={10} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Row 1 - 4 Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          marginBottom: '20px',
        }}
      >
        {/* Card 1: Projects */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <FaProjectDiagram size={14} color="#64748b" />
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#021a4a' }}>Projects</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={rowStyle}>
              <span style={{ color: '#64748b' }}>In execution</span>
              <span style={{ fontWeight: 600, color: '#021a4a' }}>{summary.projects.IN_EXECUTION}</span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: '#64748b' }}>Completed</span>
              <span style={{ fontWeight: 600, color: '#059669' }}>{summary.projects.COMPLETED}</span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: '#64748b' }}>Lead / Planning</span>
              <span style={{ fontWeight: 600, color: '#64748b' }}>{summary.projects.LEAD + summary.projects.OFFER_CREATED}</span>
            </div>
            <div style={{ ...rowStyle, marginBottom: 0, paddingTop: '6px', borderTop: '1px solid #e2e8f0' }}>
              <span style={{ color: '#64748b', fontWeight: 500 }}>Total</span>
              <span style={{ fontWeight: 700, color: '#021a4a' }}>{summary.projects.total}</span>
            </div>
          </div>
        </div>

        {/* Card 2: Offers */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <FaFileInvoiceDollar size={14} color="#64748b" />
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#021a4a' }}>Offers</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={rowStyle}>
              <span style={{ color: '#64748b' }}>Accepted</span>
              <span style={{ fontWeight: 600, color: '#059669' }}>{summary.offers.counts.ACCEPTED}</span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: '#64748b' }}>Rejected</span>
              <span style={{ fontWeight: 600, color: '#dc2626' }}>{summary.offers.counts.REJECTED}</span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: '#64748b' }}>Open pipeline</span>
              <span style={{ fontWeight: 600, color: '#021a4a' }}>
                {BusinessPartnerSalesService.formatCurrency(summary.offers.amounts.OPEN_PIPELINE)}
              </span>
            </div>
            {summary.offers.last_offer && (
              <div
                style={{ ...rowStyle, marginBottom: 0, paddingTop: '6px', borderTop: '1px solid #e2e8f0', cursor: 'pointer' }}
                onClick={() => navigate(`/offers/${summary.offers.last_offer!.id}`)}
              >
                <span style={{ color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Last offer <FaExternalLinkAlt size={9} />
                </span>
                <span style={{ fontWeight: 600, color: '#3b82f6' }}>{summary.offers.last_offer.offer_number}</span>
              </div>
            )}
          </div>
        </div>

        {/* Card 3: Invoices */}
        <div
          style={{
            ...cardStyle,
            borderLeft: (summary.invoices.amounts.overdue ?? 0) > 0 ? '3px solid #dc2626' : '1px solid #e2e8f0',
          }}
        >
          <div style={cardHeaderStyle}>
            <FaFileInvoice size={14} color="#64748b" />
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#021a4a' }}>Invoices</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={rowStyle}>
              <span style={{ color: '#64748b' }}>Total invoiced</span>
              <span style={{ fontWeight: 600, color: '#021a4a' }}>
                {BusinessPartnerSalesService.formatCurrency(summary.invoices.amounts.total_invoiced)}
              </span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: '#64748b' }}>Paid</span>
              <span style={{ fontWeight: 600, color: '#059669' }}>
                {BusinessPartnerSalesService.formatCurrency(summary.invoices.amounts.paid)}
              </span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: '#64748b' }}>Open</span>
              <span style={{ fontWeight: 600, color: '#f59e0b' }}>
                {BusinessPartnerSalesService.formatCurrency(summary.invoices.amounts.open)}
              </span>
            </div>
            <div style={{ ...rowStyle, marginBottom: 0 }}>
              <span style={{ color: (summary.invoices.amounts.overdue ?? 0) > 0 ? '#dc2626' : '#64748b' }}>
                Overdue {summary.invoices.counts.overdue > 0 && `(${summary.invoices.counts.overdue})`}
              </span>
              <span style={{ fontWeight: 600, color: (summary.invoices.amounts.overdue ?? 0) > 0 ? '#dc2626' : '#64748b' }}>
                {BusinessPartnerSalesService.formatCurrency(summary.invoices.amounts.overdue)}
              </span>
            </div>
          </div>
        </div>

        {/* Card 4: Payment Behavior */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#021a4a' }}>Payment Behavior</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {/* Emoji + Label */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <span style={{ fontSize: '28px' }}>
                {BusinessPartnerSalesService.getPaymentBehaviorEmoji(summary.payment_behavior.icon)}
              </span>
              <span style={{ fontSize: '16px', fontWeight: 700, color: paymentColor.text, textTransform: 'capitalize' }}>
                {summary.payment_behavior.label}
              </span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: '#64748b' }}>Overdue ratio</span>
              <span style={{ fontWeight: 600, color: '#021a4a' }}>{overdueRatioPercent}%</span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: '#64748b' }}>Score</span>
              <span style={{ fontWeight: 600, color: '#021a4a' }}>{summary.payment_behavior.score}/100</span>
            </div>
            <div style={{ ...rowStyle, marginBottom: 0 }}>
              <span style={{ color: '#64748b' }}>Overdue invoices</span>
              <span style={{ fontWeight: 600, color: '#021a4a' }}>
                {summary.payment_behavior.details.overdue_invoices} of {summary.payment_behavior.details.total_invoices}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Recurring Revenue Row */}
      {summary.service_contracts.active_count > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            marginBottom: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FaSync size={18} color="#0284c7" />
            <div>
              <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                Recurring Revenue
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#021a4a' }}>
                {BusinessPartnerSalesService.formatCurrency(summary.service_contracts.mrr_gross)}/month
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span
              style={{
                padding: '4px 10px',
                background: '#0284c7',
                color: 'white',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 600,
              }}
            >
              {summary.service_contracts.active_count} active contract{summary.service_contracts.active_count !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}

      {/* Service Contracts List */}
      {summary.service_contracts.items.length > 0 && (
        <div
          style={{
            paddingBottom: '16px',
            marginBottom: '16px',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#021a4a' }}>Active Service Contracts</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {summary.service_contracts.items.slice(0, 3).map((contract) => (
              <div
                key={contract.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 14px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                }}
              >
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#021a4a' }}>{contract.name}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    Started {formatDate(contract.start_date)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#0c4a6e' }}>
                    {BusinessPartnerSalesService.formatCurrency(contract.amount_gross)}
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>
                    /{contract.interval_unit === 'MONTH' ? 'month' : contract.interval_unit === 'QUARTER' ? 'quarter' : 'year'}
                  </div>
                </div>
              </div>
            ))}
            {summary.service_contracts.items.length > 3 && (
              <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center', marginTop: '4px' }}>
                +{summary.service_contracts.items.length - 3} more contracts
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment Plans Section */}
      {summary.payment_plans.plans > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            marginBottom: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FaCalendarCheck size={18} color="#7c3aed" />
            <div>
              <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                Payment Plans
              </div>
              <div style={{ fontSize: '14px', color: '#021a4a', marginTop: '2px' }}>
                {summary.payment_plans.plans} plan{summary.payment_plans.plans !== 1 ? 's' : ''} · {summary.payment_plans.milestones_total} milestone{summary.payment_plans.milestones_total !== 1 ? 's' : ''}{' '}
                <span style={{ color: '#64748b' }}>
                  ({summary.payment_plans.milestones_invoiced} invoiced, {summary.payment_plans.milestones_remaining} open)
                </span>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              Planned: <span style={{ fontWeight: 700, color: '#021a4a' }}>{BusinessPartnerSalesService.formatCurrency(summary.payment_plans.planned_gross_total)}</span>
            </div>
            <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
              Remaining: <span style={{ fontWeight: 600, color: '#021a4a' }}>{BusinessPartnerSalesService.formatCurrency(summary.payment_plans.planned_gross_remaining)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Row 2 - Tasks Quick Link */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '16px',
          borderTop: '1px solid #e2e8f0',
          marginBottom: '20px',
        }}
      >
        <button
          onClick={onScrollToTasks}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 18px',
            background: overdueTasks > 0 ? '#fef2f2' : openTasks > 0 ? '#fef3c7' : '#f1f5f9',
            border: overdueTasks > 0 ? '1px solid #fecaca' : 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
            color: overdueTasks > 0 ? '#991b1b' : openTasks > 0 ? '#92400e' : '#475569',
          }}
        >
          <FaTasks size={14} />
          <span>Open tasks: {openTasks}</span>
          {overdueTasks > 0 && (
            <span style={{ fontWeight: 700 }}>({overdueTasks} overdue)</span>
          )}
          {summary.tasks.next_due && (
            <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>
              Next due: {new Date(summary.tasks.next_due).toLocaleDateString('de-DE')}
            </span>
          )}
        </button>
      </div>

      {/* Row 3 - Recent Offers & Invoices */}
      {(recentOffers.length > 0 || recentInvoices.length > 0) && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '20px',
            paddingTop: '20px',
            borderTop: '1px solid #e2e8f0',
            marginBottom: '20px',
          }}
        >
          {/* Recent Offers */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <FaFileInvoiceDollar size={14} color="#3b82f6" />
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#021a4a' }}>Recent Offers</span>
            </div>
            {recentOffers.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>No offers yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {recentOffers.map((offer) => {
                  const statusColor = getOfferStatusColor(offer.status);
                  return (
                    <div
                      key={offer.id}
                      onClick={() => navigate(`/offers/${offer.id}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        background: '#f8fafc',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: '#021a4a' }}>
                            {offer.offer_number}
                          </span>
                          <span
                            style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: 600,
                              background: statusColor.bg,
                              color: statusColor.text,
                            }}
                          >
                            {offer.status}
                          </span>
                        </div>
                        <span
                          style={{
                            fontSize: '12px',
                            color: '#64748b',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {offer.title}
                        </span>
                      </div>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#021a4a', marginLeft: '12px' }}>
                        {BusinessPartnerSalesService.formatCurrency(offer.total_gross)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Recent Invoices */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <FaFileInvoice size={14} color="#059669" />
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#021a4a' }}>Recent Invoices</span>
            </div>
            {recentInvoices.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>No invoices yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {recentInvoices.map((invoice) => {
                  const statusColor = getInvoiceStatusColor(invoice.status);
                  return (
                    <div
                      key={invoice.id}
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        background: '#f8fafc',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#021a4a' }}>
                          {invoice.invoice_number}
                        </span>
                        <span
                          style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: 600,
                            background: statusColor.bg,
                            color: statusColor.text,
                          }}
                        >
                          {invoice.status}
                        </span>
                      </div>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#021a4a' }}>
                        {InvoiceService.formatCurrency(invoice.gross_total)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
